import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { PersonaSelectionProps } from './types';
import { PersonaCard } from './PersonaCard';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { logger } from '../../utils/logger';

const SelectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: ${theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
`;

const PersonaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' | 'outline' }>`
  padding: 1rem 2rem;
  border-radius: ${theme.borderRadius.lg};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  border: none;
  min-width: 140px;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${theme.colors.gradients.primary};
          color: ${theme.colors.text.light};
          box-shadow: ${theme.shadows.medium};
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.heavy};
            background: ${theme.colors.hover.primary};
          }
        `;
      case 'secondary':
        return `
          background: ${theme.colors.text.secondary};
          color: ${theme.colors.text.light};
          
          &:hover {
            background: ${theme.colors.text.muted};
            transform: translateY(-2px);
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: ${theme.colors.text.secondary};
          border: 2px solid ${theme.colors.border.medium};
          
          &:hover {
            border-color: ${theme.colors.primary};
            color: ${theme.colors.primary};
            transform: translateY(-2px);
          }
        `;
      default:
        return '';
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const SpinnerWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 0.5rem;
`;

const ErrorMessage = styled.div`
  background: rgba(220, 53, 69, 0.1);
  color: ${theme.colors.error};
  padding: 1rem;
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 2rem;
  text-align: center;
  border: 1px solid ${theme.colors.error};
`;

const SkipNote = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: 0.9rem;
  text-align: center;
  margin-top: 1rem;
  font-style: italic;
`;

export const PersonaSelection: React.FC<PersonaSelectionProps> = ({
  // logger.log('PersonaSelection render - selectedPersona:', selectedPersona);
  personas,
  selectedPersona,
  onPersonaSelect,
  onNext,
  onSkip,
  loading = false,
  hideNavigation = false
}) => {
  logger.log('PersonaSelection render - selectedPersona:', selectedPersona);
  const [error, setError] = useState<string>('');

  const handleNext = () => {
    if (!selectedPersona) {
      setError('Please select a persona to continue');
      return;
    }
    setError('');
    onNext();
  };

  const handleSkip = () => {
    setError('');
    onSkip();
  };

  return (
    <SelectionContainer>
      {!hideNavigation && (
        <Header>
          <Title>Choose Your Dating Persona</Title>
          <Subtitle>
            Select the persona that best represents you. This helps us create better matches
            and personalize your experience. Don't worry - you can always change this later!
          </Subtitle>
        </Header>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <PersonaGrid>
        {personas.map(persona => (
          <PersonaCard
            key={persona.id}
            persona={persona}
            selected={selectedPersona === persona.id}
            onSelect={onPersonaSelect}
            disabled={loading}
          />
        ))}
      </PersonaGrid>

      {!hideNavigation && (
        <>
          <ActionButtons>
            <Button
              variant="outline"
              onClick={handleSkip}
              disabled={loading}
            >
              Skip for Now
            </Button>

            <Button
              variant="primary"
              onClick={handleNext}
              disabled={loading || !selectedPersona}
            >
              {loading && (
                <SpinnerWrapper>
                  <LoadingSpinner size="small" color={theme.colors.text.light} />
                </SpinnerWrapper>
              )}
              Continue
            </Button>
          </ActionButtons>

          <SkipNote>
            You can always update your persona later in your profile settings
          </SkipNote>
        </>
      )}
    </SelectionContainer>
  );
};
